import instance from './instance';
import {
    AnalyticsDataResponse,
    UploadState,
    UserDetails,
    CommentActivity,
    StatusChangeActivity,
    UploadActivity,
    FileFormat,
    LabelReviewData,
    ApprovalStatus,
    ProjectLibraryResponse,
    ProjectLibraryRequestParams,
    ProjectProperties,
    ProjectDetailResponse,
    LabelDetailed,
    LabelResponse,
    PackCopyDetailed,
    PackCopyResponse,
    PackCopyIngestData,
    SpecDetail,
    PackCopyPostRequestAutomatic,
    PackCopyPostRequestManual,
    PackCopyIngestMode,
} from '../types';

import { MutableRefObject } from 'react';

export const fetchAccessToken = async (code: string) => {
    try {
        await instance.get(`/access_token?code=${code}`);
    } catch (error) {
        console.error(error);
        throw error;
    }
};

export const fetchSymbolUrl = async (symbolUrl: string): Promise<string> => {
    try {
        const response = await instance.get(`/symbol/${symbolUrl}`);
        if (response.data) {
            return response.data;
        } else {
            throw new Error('Symbol not found');
        }
    } catch (error) {
        console.error(error);
        throw error;
    }
};

export const validateToken = async () => {
    try {
        await instance.get('/validate_token');
    } catch (error) {
        console.error(error);
        throw error;
    }
};

export const fetchLabelDetails = async (id: string): Promise<LabelResponse> => {
    try {
        const response = await instance.get<LabelResponse>(`/labels/${id}`);
        if (response.data) {
            return response.data;
        } else {
            throw new Error('Label not found');
        }
    } catch (error) {
        console.error('Error fetching label details:', error);
        throw error;
    }
};

export const fetchPackCopyDetails = async (
    id: string,
): Promise<PackCopyResponse> => {
    try {
        const response = await instance.get<PackCopyResponse>(
            `/pack-copies/${id}`,
        );
        if (response.data) {
            return response.data;
        } else {
            throw new Error('Pack copy not found');
        }
    } catch (error) {
        console.error('Error fetching pack copy details:', error);
        throw error;
    }
};

// export const submitFeedbackForItem = async (
//     id: string,
//     ruleId: string,
//     feedbackdata: {
//         comment: string;
//         new_outcome: string;
//     },
// ): Promise<AssetDetailResponse> => {
//     try {
//         const response = await instance.post<AssetDetailResponse>(
//             `/feedback/${id}/${ruleId}`,
//             feedbackdata,
//         );
//         return response.data;
//     } catch (error) {
//         console.error('Error updating item feedback:', error);
//         throw error;
//     }
// };

export const deleteLabel = async (labelId: string) => {
    // todo update to pass in actual item id
    try {
        await instance.delete(`/labels/${labelId}`);
    } catch (error) {
        console.error('Error deleting label:', error);
        throw error;
    }
};

export const fetchConfigData = async () => {
    try {
        const response = await instance.get(`/config`);
        return response.data;
    } catch (error) {
        console.error(error);
        throw error;
    }
};

export const fetchProjectLibraryData = async ({
    pageNumber = 1,
    sortField = 'id',
    sortOrder = 'ASC',
    search,
    numItems,
    filters,
}: ProjectLibraryRequestParams): Promise<ProjectLibraryResponse> => {
    try {
        const params: Record<string, string | number> = {
            page_number: pageNumber,
            page_size: numItems,
            sort_field: sortField,
            sort_order: sortOrder,
            filters: JSON.stringify(filters),
        };

        if (search) {
            params.search = search;
        }

        const response = await instance.get(`/projects`, { params });

        return response.data;
    } catch (error) {
        console.error(error);
        throw new Error('Error fetching projects');
    }
};

export const fetchProjectDetails = async (
    projectId: string,
): Promise<ProjectDetailResponse> => {
    try {
        const response = await instance.get<ProjectDetailResponse>(
            `/projects/${projectId}`,
        );

        return response.data;
    } catch (error) {
        console.error(error);
        throw new Error('Error fetching project details');
    }
};

export const fetchAnalyticsData = async (
    filters: Record<string, string[]>,
): Promise<AnalyticsDataResponse> => {
    try {
        const params = {
            filters: JSON.stringify(filters),
        };
        const response = await instance.get<AnalyticsDataResponse>(
            `/analytics`,
            {
                params,
            },
        );

        return response.data;
    } catch (error) {
        console.error(error);
        throw new Error('Error fetching compliance data');
    }
};

export const waitForWebSocketMessage = (
    websocket: MutableRefObject<WebSocket | null>,
    imageId: string,
    timeout: number, // default timeout of 30 seconds
): Promise<void> => {
    return new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
            reject(new Error('Timeout waiting for WebSocket message'));
        }, timeout);

        const messageListener = (event: MessageEvent) => {
            if (event.data === `success, ${imageId}`) {
                clearTimeout(timeoutId);
                websocket.current?.removeEventListener(
                    'message',
                    messageListener,
                );
                resolve();
            } else if (event.data === `error, ${imageId}`) {
                clearTimeout(timeoutId);
                websocket.current?.removeEventListener(
                    'message',
                    messageListener,
                );
                reject(new Error('Error from websocket'));
            }
        };

        websocket.current?.addEventListener('message', messageListener);

        // Handle WebSocket closure
        websocket.current?.addEventListener('close', () => {
            clearTimeout(timeoutId);
            reject(new Error('WebSocket closed before receiving the message'));
        });
    });
};

export const fetchLabelUploadToken = async (assetName: string) => {
    const params = {
        asset_name: assetName,
    };

    try {
        const { data } = await instance.get<{ url: string; unique_id: string }>(
            '/label_upload_token',
            { params },
        );
        return data;
    } catch (error) {
        console.error(error);
        throw new Error('Error fetching upload token');
    }
};

export const fetchUploadToken = async (
    fileFormat: FileFormat,
    assetName: string,
    assetType: 'pack-copy' | 'label',
    projectId: string,
) => {
    const params = {
        file_format: fileFormat,
        asset_name: assetName,
        asset_type: assetType,
        project_id: projectId,
    };
    try {
        const { data } = await instance.get<{
            url: string;
            unique_id: string;
        }>('/upload_token', { params });
        return data;
    } catch (error) {
        console.error(error);
        throw new Error('Error fetching upload token');
    }
};

export const fetchUploadState = async () => {
    try {
        const { data } = await instance.get<UploadState[]>('/upload_state');
        return data;
    } catch (error) {
        console.error(error);
        throw new Error('Error fetching upload state');
    }
};

export const reviewLabel = async (
    labelId: string,
    reviewData: LabelReviewData,
): Promise<void> => {
    try {
        const response = await instance.post<void>(
            `/labels/${labelId}`,
            reviewData,
        );
        return response.data;
    } catch (error) {
        console.error('Error processing specification:', error);
        throw error;
    }
};

export const postComment = async (
    labelId: string,
    comment: { content: string },
): Promise<(StatusChangeActivity | CommentActivity | UploadActivity)[]> => {
    try {
        const response = await instance.put<
            (StatusChangeActivity | CommentActivity | UploadActivity)[]
        >(`/labels/${labelId}/comment`, comment);
        return response.data;
    } catch (error) {
        console.error('Error posting comment:', error);
        throw error;
    }
};

export const createProject = async (
    projectProperties: ProjectProperties,
): Promise<string> => {
    try {
        const response = await instance.post<string>(
            '/projects',
            projectProperties,
        );
        return response.data;
    } catch (error) {
        console.error('Error creating project:', error);
        throw error;
    }
};

export const changeApprovalStatus = async (
    labelId: string,
    statusChange: { new_status: ApprovalStatus; reason?: string },
): Promise<LabelResponse> => {
    try {
        const response = await instance.patch<LabelResponse>(
            `/labels/${labelId}/status`,
            statusChange,
        );
        return response.data;
    } catch (error) {
        console.error('Error changing approval status:', error);
        throw error;
    }
};

export const reviewStage = async (
    labelId: string,
    stageId: string,
    reviewStageRequest: {
        status: ApprovalStatus.APPROVED | ApprovalStatus.REJECTED;
        reason?: string;
    },
): Promise<LabelResponse> => {
    try {
        const response = await instance.post<LabelResponse>(
            `/labels/${labelId}/stages/${stageId}`,
            reviewStageRequest,
        );
        return response.data;
    } catch (error) {
        console.error('Error reviewing stage:', error);
        throw error;
    }
};

// export const provideUserFeedback = async (
//     assetId: string,
//     ruleId: string,
//     ruleFeedback: { reason: string },
// ): Promise<AssetDetailResponse> => {
//     try {
//         const response = await instance.patch<AssetDetailResponse>(
//             `/assets/${assetId}/rules/${ruleId}/feedback`,
//             ruleFeedback,
//         );
//         return response.data;
//     } catch (error) {
//         console.error('Error providing feedback:', error);
//         throw error;
//     }
// };

export const postHideUploadState = async (
    assetId: string,
): Promise<UploadState[]> => {
    try {
        const response = await instance.post<UploadState[]>(
            `/upload_state/${assetId}/hide`,
        );
        return response.data;
    } catch (error) {
        console.error('Error processing specification:', error);
        throw error;
    }
};

export const fetchUserDetails = async (
    userId: string,
): Promise<UserDetails | null> => {
    try {
        const response = await instance.get(`/user/${userId}`);
        return response.data;
    } catch (error) {
        console.error(error);
        throw error;
    }
};

// export const uploadAsset = async (
//     file: File,
//     uploadItem: FileUploadItem,
//     setUploadProgressArray: (
//         value: React.SetStateAction<UploadProgress[]>,
//     ) => void,
//     uploadProgressArrayRef: React.MutableRefObject<UploadProgress[]>,
//     newUploadProgress: UploadProgress,
// ): Promise<void> => {
//     setUploadProgressArray([
//         ...uploadProgressArrayRef.current,
//         newUploadProgress,
//     ]);
//     const uploadData = await fetchUploadData();

//     try {
//         const assetId = uploadData.asset_id;
//         newUploadProgress.imageId = assetId;
//         uploadItem.image_id = assetId;
//         setUploadProgressArray(
//             uploadProgressArrayRef.current.map((item) =>
//                 item.imageUrl === newUploadProgress.imageUrl
//                     ? { ...item, progress: 0, status: 'upload' }
//                     : item,
//             ),
//         );

//         // const blob = await rotateImage(file);
//         if (file) {
//             await axios.put(uploadData.url, file, {
//                 headers: { 'x-ms-blob-type': 'BlockBlob' },
//                 onUploadProgress: (progressEvent) => {
//                     const total = progressEvent.total ?? 1;
//                     const percentage = Math.round(
//                         (progressEvent.loaded * 100) / total,
//                     );
//                     setUploadProgressArray(
//                         uploadProgressArrayRef.current.map((item) =>
//                             item.imageId === uploadData.asset_id
//                                 ? { ...item, progress: percentage }
//                                 : item,
//                         ),
//                     );
//                 },
//             });
//         } else {
//             throw new Error('Blob generation failed.');
//         }
//     } catch (error) {
//         console.error('Upload failed:', error);
//         throw new Error('Image upload failed.');
//     }
//     setUploadProgressArray(
//         uploadProgressArrayRef.current.map((item) =>
//             item.imageId === uploadData.asset_id
//                 ? {
//                       ...item,
//                       status: 'checking',
//                       progress: 0,
//                       timeStamp: new Date(),
//                   }
//                 : item,
//         ),
//     );

// try {
//     const result: UploadResult = (
//         await instance.post('/upload_file_meta', uploadItem)
//     ).data;

//     console.log('response', result);
// } catch (error) {
//     console.error('Metadata upload failed:', error);
//     setUploadProgressArray((prevProgressArray) =>
//         prevProgressArray.map((item) =>
//             item.imageId === uploadData.image_id
//                 ? { ...item, status: 'error', progress: 100 }
//                 : item,
//         ),
//     );
// }
// };

export const postPackCopyAutomatic = async (
    packCopyId: string,
    ingestData: PackCopyIngestData,
): Promise<void> => {
    try {
        const requestData: PackCopyPostRequestAutomatic = {
            mode: PackCopyIngestMode.AUTOMATIC,
            properties: ingestData.properties,
            data: {
                file_config: ingestData.file_config,
                pack_copy_id: packCopyId,
            },
        };

        const response = await instance.post<void>(`/pack-copies`, requestData);
        return response.data;
    } catch (error) {
        console.error('Error processing pack copy:', error);
        throw error;
    }
};

export const postPackCopyManual = async (
    properties: { version_name: string; notes?: string; project_id: string },
    specs: SpecDetail[],
): Promise<string> => {
    try {
        const requestData: PackCopyPostRequestManual = {
            mode: PackCopyIngestMode.MANUAL,
            properties,
            data: specs,
        };

        const response = await instance.post<string>(
            `/pack-copies`,
            requestData,
        );
        return response.data;
    } catch (error) {
        console.error('Error processing pack copy:', error);
        throw error;
    }
};

export const deletePackCopy = async (packCopyId: string): Promise<void> => {
    try {
        await instance.delete(`/pack-copies/${packCopyId}`);
    } catch (error) {
        console.error('Error deleting pack copy:', error);
        throw error;
    }
};

export const putPackCopySpec = async (
    packCopyId: string,
    specs: SpecDetail[],
): Promise<PackCopyResponse> => {
    try {
        const response = await instance.put<PackCopyResponse>(
            `/pack-copies/${packCopyId}/specs/${specs[0].name}`,
            specs,
        );
        return response.data;
    } catch (error) {
        console.error('Error updating pack copy specification:', error);
        throw error;
    }
};

export const postPackCopySpec = async (
    packCopyId: string,
    specs: SpecDetail[],
): Promise<PackCopyResponse> => {
    try {
        const response = await instance.post<PackCopyResponse>(
            `/pack-copies/${packCopyId}/specs`,
            specs,
        );
        return response.data;
    } catch (error) {
        console.error('Error creating pack copy specification:', error);
        throw error;
    }
};

export const deletePackCopySpec = async (
    packCopyId: string,
    specName: string,
): Promise<PackCopyResponse> => {
    try {
        const response = await instance.delete<PackCopyResponse>(
            `/pack-copies/${packCopyId}/specs/${specName}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error deleting pack copy specification:', error);
        throw error;
    }
};

export const updatePackCopyName = async (
    packCopyId: string,
    name: string,
): Promise<PackCopyResponse> => {
    try {
        const response = await instance.put<PackCopyResponse>(
            `/pack-copies/${packCopyId}/name`,
            { name }, // Sending name as a JSON object
        );
        return response.data;
    } catch (error) {
        console.error('Error updating pack copy name:', error);
        throw error;
    }
};

export const updatePackCopyNotes = async (
    packCopyId: string,
    notes: string,
): Promise<PackCopyResponse> => {
    try {
        const response = await instance.put<PackCopyResponse>(
            `/pack-copies/${packCopyId}/notes`,
            { notes }, // Sending notes as a JSON object
        );
        return response.data;
    } catch (error) {
        console.error('Error updating pack copy notes:', error);
        throw error;
    }
};

export const duplicatePackCopy = async (
    packCopyId: string,
): Promise<PackCopyResponse> => {
    try {
        const response = await instance.post<PackCopyResponse>(
            `/pack-copies/${packCopyId}/duplicate`,
        );
        return response.data;
    } catch (error) {
        console.error('Error duplicating pack copy:', error);
        throw error;
    }
};

export const updateLabelName = async (
    labelId: string,
    name: string,
): Promise<LabelResponse> => {
    try {
        const response = await instance.put<LabelResponse>(
            `/labels/${labelId}/name`,
            { name }, // Sending name as a JSON object
        );
        return response.data;
    } catch (error) {
        console.error('Error updating label name:', error);
        throw error;
    }
};

export const updateLabelNotes = async (
    labelId: string,
    notes: string,
): Promise<LabelResponse> => {
    try {
        const response = await instance.put<LabelResponse>(
            `/labels/${labelId}/notes`,
            { notes }, // Sending notes as a JSON object
        );
        return response.data;
    } catch (error) {
        console.error('Error updating label notes:', error);
        throw error;
    }
};
